import { IComponentStylesBase } from '../../models/IComponentStylesBase.js';
import { IConfigurationBase } from '../../models/IConfigurationBase.js';

import { StoreBase } from './StoreBase.js';

/**
 * The instance store that extends this base class should be implemented within the setupStoreContext method.
 *
 * @export
 * @abstract
 * @class ClientConfigurationStoreBase
 * @template TClientConfig
 */
export abstract class ClientConfigurationStoreBase<
	TClientConfig,
	TConfiguration extends IConfigurationBase,
	TComponentStyles extends IComponentStylesBase,
> extends StoreBase<TConfiguration, TComponentStyles> {
	/**
	 * The Client Configuration Code
	 *
	 * @protected
	 */
	protected abstract code: string;
	/**
	 * The Client Configuration field to extract from the raw response
	 *
	 * @protected
	 * @default 'values'
	 */
	protected key = 'values';

	/**
	 * Call this method as part of the AppStore.customInitializeAfter method.
	 *
	 * @param callbacks
	 * @returns
	 */
	public async initialize(callbacks?: Record<string, EventListener>): Promise<void> {
		this.query();
	}

	/**
	 * Will automatically be called by the initialize method to fetch the client configuration based on the key defined in the instance class.
	 *
	 * @private
	 * @returns
	 */
	private query() {
		if (this.enabled) {
			const filterMappingConfig: TClientConfig = this.getQueryValue({
				queryOptionsType: 'clientConfiguration',
				code: this.code,
				key: this.key,
			});
			if (filterMappingConfig) {
				return this.orchestrate(filterMappingConfig);
			}
		}
	}

	/**
	 * This method should consume the client configuration API response and do what is needed with it, e.g. save it on the instance class as a class param or push it to another store.
	 *
	 * @protected
	 * @abstract
	 * @param config
	 */
	protected abstract orchestrate(config: TClientConfig): void;
	/**
	 * This method should be used to disable the fetching and usage of this client configuration.
	 *
	 * @protected
	 * @abstract
	 * @returns boolean
	 */
	public abstract get enabled(): boolean;
}
